import type { Feed, FeedScore, FeedVitals, StandardConfig } from './types.js';
import { ingestAdmits } from './types.js';
import type { Observation } from './observation.js';
import { reasonText } from './observation.js';
import { DEFAULT_CASCADE } from './cascade.js';

/**
 * The standard, as code. Pure and dependency free so the publisher phone can
 * run it to show an operator why they are not on air, and the server can run
 * the identical logic to decide. Same numbers on both sides by construction.
 *
 * Two layers, deliberately:
 *   1. Hard gates. Fail any one and the feed is ineligible, no matter how good
 *      the rest looks. This is what makes it a standard rather than a ranking.
 *   2. A continuous score, used only to choose among feeds that already pass.
 */

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

/** 1.0 inside [lo, hi], falling off linearly over `slack` outside it. */
function band(value: number, lo: number, hi: number, slack: number): number {
  if (value >= lo && value <= hi) return 1;
  const distance = value < lo ? lo - value : value - hi;
  return clamp01(1 - distance / slack);
}

/** 1.0 at or below `good`, 0 at or above `bad`. */
function decay(value: number, good: number, bad: number): number {
  if (value <= good) return 1;
  if (value >= bad) return 0;
  return clamp01(1 - (value - good) / (bad - good));
}

/** 0 at or below `bad`, 1.0 at or above `good`. */
function ramp(value: number, bad: number, good: number): number {
  if (value >= good) return 1;
  if (value <= bad) return 0;
  return clamp01((value - bad) / (good - bad));
}

export function scoreFeed(v: FeedVitals, s: StandardConfig): FeedScore {
  const failures: string[] = [];

  // --- hard gates -----------------------------------------------------------
  if (v.bitrate < s.minBitrate) {
    failures.push(`bitrate ${Math.round(v.bitrate / 1000)}kbps below ${s.minBitrate / 1000}kbps`);
  }
  if (v.packetLoss > s.maxPacketLoss) {
    failures.push(`packet loss ${(v.packetLoss * 100).toFixed(1)}% above ${s.maxPacketLoss * 100}%`);
  }
  if (v.fps < s.minFps) {
    failures.push(`${v.fps.toFixed(0)}fps below ${s.minFps}fps`);
  }
  if (v.frameHeight < s.minHeight) {
    failures.push(`${v.frameWidth}x${v.frameHeight} below ${s.minHeight}p`);
  }
  if (v.luma < s.lumaRange[0]) failures.push('picture too dark');
  if (v.luma > s.lumaRange[1]) failures.push('picture blown out');
  if (v.clipping > s.maxClipping) failures.push('picture clipping');
  if (v.shake > s.maxShake) failures.push('camera too shaky');
  // The gate is "is anything being captured", not "is the room loud enough".
  // A quiet room is a room; a dead microphone is a broken instrument, and only
  // the second is a standards failure. Gating on RMS conflated them and refused
  // working feeds for being indoors: `audioRmsRange` now ranks, below.
  if (!v.audioTrackLive) failures.push('no audio track');
  if (v.audioRms > s.audioRmsRange[1]) failures.push('audio too hot');
  if (v.audioClipping > s.maxAudioClipping) failures.push('audio clipping');

  // --- continuous score, used only to rank feeds that already pass ----------
  const transport =
    0.4 * ramp(v.bitrate, s.minBitrate, s.minBitrate * 4) +
    0.3 * decay(v.packetLoss, 0.005, s.maxPacketLoss) +
    0.2 * ramp(v.fps, s.minFps, 30) +
    0.1 * decay(v.rttMs ?? 200, 40, 400);

  const picture =
    0.45 * band(v.luma, s.lumaRange[0] + 0.08, s.lumaRange[1] - 0.08, 0.2) +
    0.35 * decay(v.shake, s.maxShake * 0.3, s.maxShake) +
    0.2 * decay(v.clipping, 0.02, s.maxClipping);

  const sound =
    0.7 * band(v.audioRms, s.audioRmsRange[0] * 4, s.audioRmsRange[1] * 0.8, 0.15) +
    0.3 * decay(v.audioClipping, 0.005, s.maxAudioClipping);

  // Transport is weighted hardest because a feed that buffers is not a feed.
  const overall = clamp01(0.45 * transport + 0.35 * picture + 0.2 * sound);

  return {
    overall,
    transport: clamp01(transport),
    picture: clamp01(picture),
    sound: clamp01(sound),
    passes: failures.length === 0,
    failures,
  };
}

/**
 * Is a verdict currently usable?
 *
 * Three ways to fail, all treated identically: never reviewed, reviewed and
 * rejected, or reviewed too long ago. Absence is not approval.
 */
export function verdictHolds(v: Observation | null, now: number, s: StandardConfig): boolean {
  if (v === null) return false;
  if (!v.approved) return false;
  if (v.error !== undefined) return false;
  // A local model's approval buys one cycle, not standing consent. A frontier
  // ruling taken from the DESCRIBER's words rather than the frames is cheap in
  // the same way and expires on the same clock: it inherits whatever the
  // describer failed to mention, so it must not still authorise a feed six
  // windows later when that feed has become a challenger.
  const cheap = v.decidedBy === 'local' || v.fromDescription === true;
  const ttl = cheap ? DEFAULT_CASCADE.localTtlMs : s.verdictTtlMs;
  return now - v.sampledAt <= ttl;
}

/**
 * The blockers that belong to NEITHER modality, before the editorial gates.
 *
 * Gate 0. A feed that is not reporting, not scored, or mechanically failing
 * supplies no picture and no sound, so this appears in both halves rather than
 * in one.
 */
function beforeGates(feed: Feed, now: number, s: StandardConfig): string[] {
  const blockers: string[] = [];
  if (feed.lastVitalsAt === null || now - feed.lastVitalsAt > s.staleAfterMs) {
    blockers.push('publisher not reporting');
  } else if (feed.score === null) {
    blockers.push('not scored yet');
  } else if (!feed.score.passes) {
    blockers.push(...feed.score.failures);
  }
  return blockers;
}

/** Gate 1, and nothing else. */
function pictureBlockers(feed: Feed, now: number, s: StandardConfig): string[] {
  if (verdictHolds(feed.visual, now, s)) return [];
  return [
    feed.visual === null
      ? 'awaiting picture review'
      : feed.visual.error !== undefined
        ? `picture review failed: ${feed.visual.error}`
        : !feed.visual.approved
          ? `picture: ${feed.visual.reasons.map(reasonText).join(', ')}`
          : 'picture review stale',
  ];
}

/** Gate 2, and nothing else. */
function speechBlockers(feed: Feed, now: number, s: StandardConfig): string[] {
  if (verdictHolds(feed.speech, now, s)) return [];
  return [
    feed.speech === null
      ? 'awaiting speech review'
      : feed.speech.error !== undefined
        ? `speech review failed: ${feed.speech.error}`
        : !feed.speech.approved
          ? `speech: ${feed.speech.reasons.map(reasonText).join(', ')}`
          : 'speech review stale',
  ];
}

/**
 * The blockers that belong to NEITHER modality, after the editorial gates.
 *
 * Ingest provenance and the audience gate. Both remove a feed entirely rather
 * than one of its tracks: an indirect publish is a third party's re-encode of
 * both, and an audience blocking a feed is blocking what they watched, which
 * was the whole thing.
 */
function afterGates(feed: Feed): string[] {
  const blockers: string[] = [];

  // Ingest provenance. The GATE that refuses lives in P1, which will not sample a
  // feed that is not a direct publish, so such a feed never gets a verdict and
  // falls out through the two clauses above. This clause exists so the publisher
  // is told WHY rather than being left on "awaiting picture review" forever,
  // which is proposal 2.4's failure wearing a different hat. Unchecked is not
  // reported as a failure here: absence of a check is P1's business, and
  // reporting it twice would double-count one condition against the budget.
  if (feed.ingest !== null && !ingestAdmits(feed.ingest)) {
    blockers.push(`ingest: ${feed.ingest.reason}`);
  }

  // The audience gate is post hoc by nature, so it only ever removes approval;
  // it never grants it. A feed with no ratings at all is not blocked.
  if (feed.audience.blocked) {
    const pct = (feed.audience.lowerBound * 100).toFixed(0);
    blockers.push(
      `audience: ${feed.audience.bad} of ${feed.audience.bad + feed.audience.good} rated bad ` +
        `across ${feed.audience.raters} viewers (at least ${pct}% bad with 95% confidence)`,
    );
  }

  return blockers;
}

/**
 * May this feed supply the PICTURE?
 *
 * Gates 0, 1 and 3. Not gate 2: what was said has no bearing on whether the
 * camera is doing its job, which is the same reason the two gates are reviewed
 * blind to each other.
 *
 * `docs/plans/2026-08-30-picture-and-sound-as-two-decisions.md` stage 1. Adding
 * this changed no behaviour: `eligibility()` below is still what every caller
 * asks, and still answers exactly what it answered before. Guard `SND1`.
 */
export function pictureEligibility(
  feed: Feed,
  now: number,
  s: StandardConfig,
): { eligible: boolean; blockers: string[] } {
  const blockers = [
    ...beforeGates(feed, now, s),
    ...pictureBlockers(feed, now, s),
    ...afterGates(feed),
  ];
  return { eligible: blockers.length === 0, blockers };
}

/**
 * May this feed supply the SOUND?
 *
 * Gates 0, 2 and 3. Not gate 1.
 *
 * **This is eligibility, not desirability.** A feed whose microphone is off
 * passes here, correctly, because no speech in a window is a legitimate local
 * approval and nothing was said that breaches the standard. Whether a silent
 * track is worth CHOOSING is a different question with a different predicate,
 * and answering it here would answer it with the speech gate. Issue 026.
 */
export function soundEligibility(
  feed: Feed,
  now: number,
  s: StandardConfig,
): { eligible: boolean; blockers: string[] } {
  const blockers = [
    ...beforeGates(feed, now, s),
    ...speechBlockers(feed, now, s),
    ...afterGates(feed),
  ];
  return { eligible: blockers.length === 0, blockers };
}

/**
 * May this feed go to air whole, picture and sound together?
 *
 * The conjunction of the two above, and the ONLY thing that changed when they
 * were split out is that this is now written as a composition. The blocker list
 * is the same strings in the same order, which `SND1` holds to a table covering
 * every path, because a refusal a publisher reads is a published artifact and
 * reordering one is a change to what they were told.
 */
export function eligibility(
  feed: Feed,
  now: number,
  s: StandardConfig,
): { eligible: boolean; blockers: string[] } {
  const blockers = [
    ...beforeGates(feed, now, s),
    ...pictureBlockers(feed, now, s),
    ...speechBlockers(feed, now, s),
    ...afterGates(feed),
  ];
  return { eligible: blockers.length === 0, blockers };
}

/**
 * A frame the SERVER measured as black, or as blown out.
 *
 * Tier 0 and deterministic, and it closes a Tier 1 failure that was measured
 * rather than feared: `docs/reference/two-stage-filter.md` records the vision model
 * approving a frame it had itself described as "completely black". A model can
 * be talked out of a finding by context; a mean pixel value cannot.
 *
 * This is the SAME condition `lumaRange` already gates, with a better
 * instrument. The publisher's own canvas reported it before and the server's own
 * ffmpeg sample reports it now, so no gate is added and the section 9.1 budget
 * does not move. See `docs/reference/gate-ledger.md`.
 *
 * Every frame must be out for this to fire. One dark frame in a pan is not a
 * dark feed, and a detector that fired on it would be rejecting basketball.
 */
export function lumaFinding(
  luma: readonly number[],
  s: StandardConfig,
): { kind: 'dark' | 'blown'; mean: number } | null {
  if (luma.length === 0) return null;
  const mean = luma.reduce((a, b) => a + b, 0) / luma.length;
  const dark = luma.every((l) => l < s.lumaRange[0]);
  const blown = luma.every((l) => l > s.lumaRange[1]);
  if (dark) return { kind: 'dark', mean };
  if (blown) return { kind: 'blown', mean };
  return null;
}

/**
 * The shape a candidate takes into the selection rule.
 *
 * **`decide()` used to live here and was retired on 2026-08-29 by wave 2's P4.**
 * It was an anonymous reduce that broke ties by array order, silently, and
 * reported "took best of 1 eligible" as though a comparison had happened
 * (proposal sections 2.3 and 9.2). `shared/derivation.ts` replaced the RULE in
 * wave 1 and `server/primitives/select-broadcast.ts` replaced the CALLER in wave
 * 2, and the two coexisting is how the switch margin became two literals inside
 * a day. A second selection rule left standing is a fork waiting for a caller,
 * so it is gone rather than deprecated.
 */
export interface Candidate {
  feedId: string;
  score: FeedScore;
  stale: boolean;
  /** False unless mechanical, picture AND speech gates all currently hold. */
  eligible: boolean;
}
